import { useState } from 'react'
import { Calendar as CalendarIcon, AlertTriangle } from 'lucide-react'
import { cn } from 'shared/ui/utils'
import { Button } from 'shared/ui/button'
import { Calendar } from 'shared/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from 'shared/ui/popover'
import { validateWorkDate, getDateConstraints, parseLocalDateString } from 'modules/permit/utils/permitHelpers'

// ฟอร์แมตรูปแบบวันที่เต็ม
const formatDate = (date: Date): string => {
  const months = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ]

  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()

  return `${day} ${month} ${year}`
}

// ฟอร์แมตวันที่แบบย่อสำหรับปุ่มเลือก
const formatDateShort = (date: Date): string => {
  const months = [
    'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
    'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
  ]

  const day = date.getDate()
  const month = months[date.getMonth()]
  const year = date.getFullYear()

  return `${day} ${month} ${year}`
}

interface DatePickerProps {
  id?: string
  value?: string
  onChange: (date: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  label?: string
  required?: boolean
  error?: string
  allowPastDates?: boolean
  allowFutureDates?: boolean
  hideMaxDateHint?: boolean
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = 'เลือกวันที่',
  disabled = false,
  className,
  label,
  required = false,
  error,
  allowPastDates = false,
  allowFutureDates = false,
  hideMaxDateHint = false,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dateConstraints = getDateConstraints({ allowPastDates, allowFutureDates })

  // Convert string to Date object
  const selectedDate = value ? (parseLocalDateString(value) ?? undefined) : undefined

  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const dateString = `${year}-${month}-${day}`
      onChange(dateString)
      setIsOpen(false)
    }
  }

  // Get date constraints for calendar
  const minDate = dateConstraints.min ? (parseLocalDateString(dateConstraints.min) ?? undefined) : undefined
  const maxDate = dateConstraints.max ? (parseLocalDateString(dateConstraints.max) ?? undefined) : undefined

  // Validate current date
  const validation = value ? validateWorkDate(value, { allowPastDates, allowFutureDates }) : { isValid: true }
  const hasError = !validation.isValid || !!error
  const errorMessage = error || validation.error

  return (
    <div className="space-y-2">
      {label && (
        <label htmlFor={id} className="text-sm text-gray-600">
          {label}
          {required ? <span className="text-red-500">*</span> : null}
        </label>
      )}

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            className={cn(
              'w-full justify-start border-gray-200 !bg-background text-left font-normal',
              selectedDate && 'text-sky-700',
              !selectedDate && 'text-sky-700',
              hasError && 'border-red-500 focus:ring-red-500',
              disabled && selectedDate && 'cursor-not-allowed text-sky-700 opacity-100',
              disabled && !selectedDate && 'cursor-not-allowed opacity-100',
              className
            )}
            disabled={disabled}
          >
            <CalendarIcon
              className={cn(
                'mr-2 h-4 w-4',
                disabled && selectedDate ? 'text-sky-700' : 'text-gray-400',
              )}
            />
            {selectedDate ? (
              formatDateShort(selectedDate)
            ) : (
              <span>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-auto p-0 bg-white" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            disabled={(date) =>
              (minDate ? date < minDate : false) ||
              (maxDate ? date > maxDate : false) ||
              disabled
            }
            initialFocus
            className="rounded-md border-0"
          />

          {hideMaxDateHint || !maxDate ? null : (
            <div className="p-3 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-600">
                สามารถเลือกวันได้ถึง {formatDate(maxDate)}
              </p>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {hasError && errorMessage && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" />
          {errorMessage}
        </p>
      )}
    </div>
  )
}
